import { useState, useEffect, useCallback, useRef } from "react";
import AnalisiSistemi from "./AnalisiSistemi";
import PortfolioTrendStorico from "./PortfolioTrendStorico";
import IconaInfoModal from "../components/IconaInfoModal";

import { API } from "../config";

const btnBase = {
  fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600,
  border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",
  display:"inline-flex",alignItems:"center",gap:6,
  transition:"opacity 0.15s",
};
const btnPrimary = {...btnBase,background:"#2563EB",color:"#fff"};

// ===== HELPERS =====
function fmtPunti(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const s = (n >= 0 ? "+" : "") + n.toFixed(2);
  return s;
}
// Max P/L visualizzato senza segno + (D487 v174)
function fmtNoSegno(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(2);
}
function colPunti(v) {
  if (v > 0) return "#16A34A";
  if (v < 0) return "#DC2626";
  return "#64748B";
}
function fmtNum(v, dec=2) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(dec);
}
// Formatta la quota media di un sistema:
// - Solo Punta: "2.10"
// - Solo Banca: "3.36"
// - Multi-mercato (P+B): "2.10 / 3.45" (ordine sempre Punta / Banca)
// - Nessuna quota disponibile: "—"
function fmtQuota(qp, qb, dec=2) {
  const hasP = qp !== null && qp !== undefined;
  const hasB = qb !== null && qb !== undefined;
  if (hasP && hasB) return `${Number(qp).toFixed(dec)} / ${Number(qb).toFixed(dec)}`;
  if (hasP) return Number(qp).toFixed(dec);
  if (hasB) return Number(qb).toFixed(dec);
  return "—";
}

// Badge "Attivo" / "Dismesso" / "Mai attivato" — D482 v174, 3 stati da v213
function BadgeStato({ attivo, maiAttivato }) {
  if (!attivo && maiAttivato) {
    return (
      <span style={{
        fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:12,
        background:"#F1F5F9",color:"#94A3B8",letterSpacing:"0.04em",
        textTransform:"uppercase",whiteSpace:"nowrap",
      }}>Mai attivato</span>
    );
  }
  if (attivo) {
    return (
      <span style={{
        fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:12,
        background:"#DCFCE7",color:"#15803D",letterSpacing:"0.04em",
        textTransform:"uppercase",whiteSpace:"nowrap",
      }}>Attivo</span>
    );
  }
  return (
    <span style={{
      fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:12,
      background:"#EDE9FE",color:"#7C3AED",letterSpacing:"0.04em",
      textTransform:"uppercase",whiteSpace:"nowrap",
    }}>Dismesso</span>
  );
}

// ===== COMPONENTE CARD KPI =====
function KpiCard({ icon, label, value, color, sub }) {
  return (
    <div style={{
      background:"#fff", border:"1px solid #E8E8E4", borderRadius:10,
      padding:"16px 20px", flex:"1 1 150px", minWidth:140,
    }}>
      <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",
        letterSpacing:"0.06em",marginBottom:6}}>{icon} {label}</div>
      <div style={{fontSize:24,fontWeight:700,color:color||"#1E293B",
        fontFamily:"'IBM Plex Mono',monospace",letterSpacing:"-0.02em"}}>{value}</div>
      {sub && <div style={{fontSize:11,color:"#94A3B8",marginTop:4}}>{sub}</div>}
    </div>
  );
}

// ===== ICONA SORT =====
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span style={{color:"#CBD5E1",marginLeft:4}}>↕</span>;
  return <span style={{color:"#2563EB",marginLeft:4}}>{sortDir === "asc" ? "↑" : "↓"}</span>;
}

// ===== TABELLA SISTEMI (lista master Portfolio Sistemi — refactor v176) =====
function TabellaSistemi({ dati, onClickRiga, stagioniFiltro }) {
  const [sortCol, setSortCol] = useState("punti");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  // Calcolo KPI ricalcolati per filtro stagione (memoizzato sul singolo item)
  const getRow = (s) => {
    const sel = stagioniFiltro && stagioniFiltro.size > 0;
    if (!sel) {
      return {
        eventi: s.eventi, punti: s.punti, ppe: s.ppe,
        pct_vincenti: s.pct_vincenti,
        max_lose_run: s.max_lose_run, max_win_run: s.max_win_run,
        max_drawdown: s.max_drawdown,
        quota_media_punta: s.quota_media_punta, quota_media_banca: s.quota_media_banca,
        max_pl: s.max_pl,
        espo_max_giornaliera: s.espo_max_giornaliera,
        espo_max_settimanale: s.espo_max_settimanale,
        esp_media: s.esp_media,
      };
    }
    const acc = Object.entries(s.dati_per_stagione || {})
      .filter(([st]) => stagioniFiltro.has(st))
      .reduce((a, [, v]) => ({
        eventi: (a.eventi||0) + v.eventi,
        punti: (a.punti||0) + v.punti,
        vincenti: (a.vincenti||0) + Math.round(v.pct_vincenti/100*v.eventi),
        max_lose_run: Math.max(a.max_lose_run||0, v.max_lose_run||0),
        max_win_run: Math.max(a.max_win_run||0, v.max_win_run||0),
        max_drawdown: Math.max(a.max_drawdown||0, v.max_drawdown||0),
        qp_sum: (a.qp_sum||0) + (v.quota_media_punta != null ? v.quota_media_punta * v.eventi : 0),
        qp_ev:  (a.qp_ev||0)  + (v.quota_media_punta != null ? v.eventi : 0),
        qb_sum: (a.qb_sum||0) + (v.quota_media_banca != null ? v.quota_media_banca * v.eventi : 0),
        qb_ev:  (a.qb_ev||0)  + (v.quota_media_banca != null ? v.eventi : 0),
        esp_sum: (a.esp_sum||0) + (v.esp_media != null ? v.esp_media * v.eventi : 0),
        esp_ev:  (a.esp_ev||0)  + (v.esp_media != null ? v.eventi : 0),
        espo_gio_max: Math.max(a.espo_gio_max||0, v.espo_max_giornaliera||0),
        espo_set_max: Math.max(a.espo_set_max||0, v.espo_max_settimanale||0),
      }), {});
    const ev = acc.eventi || 0;
    const pt = Math.round((acc.punti||0) * 100) / 100;
    return {
      eventi: ev,
      punti: pt,
      ppe: ev > 0 ? Math.round(pt/ev*10000)/10000 : 0,
      pct_vincenti: ev > 0 ? Math.round((acc.vincenti||0)/ev*10000)/100 : 0,
      max_lose_run: acc.max_lose_run||0,
      max_win_run: acc.max_win_run||0,
      max_drawdown: acc.max_drawdown||0,
      quota_media_punta: acc.qp_ev > 0 ? Math.round(acc.qp_sum / acc.qp_ev * 100) / 100 : null,
      quota_media_banca: acc.qb_ev > 0 ? Math.round(acc.qb_sum / acc.qb_ev * 100) / 100 : null,
      // Max P/L con filtro stagione: approssimazione = max(0, sum punti filtrati)
      max_pl: Math.max(0, pt),
      // Esposizioni: max per Giorn./Sett., media ponderata per Esp. Media
      espo_max_giornaliera: acc.espo_gio_max || 0,
      espo_max_settimanale: acc.espo_set_max || 0,
      esp_media: acc.esp_ev > 0 ? Math.round(acc.esp_sum / acc.esp_ev * 10000) / 10000 : 0,
    };
  };

  const sorted = [...dati].sort((a, b) => {
    let va, vb;
    if (["nome","mercato","prima_stagione"].includes(sortCol)) {
      va = a[sortCol] ?? "";
      vb = b[sortCol] ?? "";
    } else if (sortCol === "attivo") {
      va = a.attivo ? 1 : 0;
      vb = b.attivo ? 1 : 0;
    } else {
      const ra = getRow(a); const rb = getRow(b);
      va = ra[sortCol] ?? 0;
      vb = rb[sortCol] ?? 0;
    }
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  // Colonne numeriche (allineamento destra)
  const colNumerica = (col) => ["eventi","punti","ppe","max_pl","quota_media","esp_media","espo_max_giornaliera","espo_max_settimanale","pct_vincenti","max_win_run","max_lose_run","max_drawdown"].includes(col);

  const thStyle = (col) => ({
    padding:"10px 12px", fontSize:11, fontWeight:700, color:"#64748B",
    textTransform:"uppercase", letterSpacing:"0.05em",
    textAlign: colNumerica(col) ? "right" : (col === "attivo" ? "center" : "left"),
    cursor:"pointer", userSelect:"none", whiteSpace:"nowrap",
    background:"#F8FAFC", borderBottom:"2px solid #E8E8E4",
  });
  const tdStyle = (align="left") => ({
    padding:"10px 12px", fontSize:13, color:"#1E293B",
    borderBottom:"1px solid #F1F5F9", textAlign:align,
    verticalAlign:"middle",
  });

  if (!dati.length) return (
    <div style={{padding:40,textAlign:"center",color:"#94A3B8",fontSize:13}}>
      Nessun sistema corrisponde ai filtri attivi.
    </div>
  );

  // Colore viola per esposizione (D482)
  const COL_VIOLA = "#7C3AED";

  return (
    <div style={{overflowX:"auto"}}>
      <table style={{width:"100%",borderCollapse:"collapse",minWidth:1200}}>
        <thead>
          <tr>
            {[
              {col:"nome",                 label:"Nome"},
              {col:"attivo",               label:"Stato"},
              {col:"mercato",              label:"Mercato"},
              {col:"eventi",               label:"Eventi"},
              {col:"punti",                label:"Punti"},
              {col:"ppe",                  label:"PPE"},
              {col:"max_pl",               label:"Max P/L"},
              {col:"quota_media",          label:"Quota Media", tooltip:"Quota Media (Punta / Banca)"},
              {col:"esp_media",            label:"Esp. Media", tooltip:"Esposizione Media per evento (rischio teorico medio)"},
              {col:"espo_max_giornaliera", label:"Esp. Max Giorn.", tooltip:"Esposizione Max Giornaliera"},
              {col:"espo_max_settimanale", label:"Esp. Max Sett.", tooltip:"Esposizione Max Settimanale (ciclo operativo mar-gio/ven-lun)"},
              {col:"pct_vincenti",         label:"Win Rate"},
              {col:"max_win_run",          label:"Max Win"},
              {col:"max_lose_run",         label:"Max Lose"},
              {col:"max_drawdown",         label:"Max DD",      tooltip:"Massimo Drawdown"},
            ].map(({col,label,tooltip}) => (
              <th key={col} style={thStyle(col)} onClick={() => handleSort(col)} title={tooltip || ""}>
                {label}<SortIcon col={col} sortCol={sortCol} sortDir={sortDir}/>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => {
            const r = getRow(s);
            const nStagioni = (s.stagioni_lista || []).length;
            const fmtD = (d) => String(d).slice(0,10).split("-").reverse().join("/");
            const dataAttiv = s.attivo === false
              ? (s.data_sospensione ? ` · dismesso dal ${fmtD(s.data_sospensione)}` : "")
              : (s.data_attivazione ? ` · attivo dal ${fmtD(s.data_attivazione)}` : "");
            const rev = s.numero_revisione > 0 ? ` · rev. ${s.numero_revisione}` : "";
            const sottotitolo = nStagioni > 0
              ? `${nStagioni} stagion${nStagioni===1?"e":"i"} · dal ${s.prima_stagione}${dataAttiv}${rev}`
              : "—";
            return (
              <tr key={s.id}
                onClick={() => onClickRiga && onClickRiga(s)}
                style={{cursor:"pointer", transition:"background 0.1s"}}
                onMouseEnter={e => e.currentTarget.style.background="#F0F7FF"}
                onMouseLeave={e => e.currentTarget.style.background=""}
              >
                {/* 1. Nome (con sottotitolo, badge 5MKO, icona commenti) */}
                <td style={tdStyle()}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontWeight:600,color:"#1E293B"}}>{s.nome}</span>
                    {s.filtri_5mko && (
                      <span title="Sistema con filtri sulle quote 5 minuti prima del Kick-Off" style={{
                        fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:8,
                        background:"#FEF3C7",color:"#92400E",letterSpacing:"0.04em",
                      }}>5MKO</span>
                    )}
                    {s.commenti && (
                      <span title={s.commenti} style={{
                        fontSize:14,color:"#94A3B8",cursor:"help",lineHeight:1,
                      }}>💬</span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:"#94A3B8",marginTop:2,fontFamily:"'IBM Plex Mono',monospace"}}>
                    {sottotitolo}
                  </div>
                </td>
                {/* 2. Stato */}
                <td style={tdStyle("center")}>
                  <BadgeStato attivo={s.attivo !== false} maiAttivato={!s.data_attivazione} />
                  {s.attivo !== false && s.filtri_modificati_il && (
                    <span title="Filtri cambiati e mai confermati come nuova revisione"
                      style={{marginLeft:6,color:"#B45309",fontSize:13,cursor:"default"}}>&#9888;</span>
                  )}
                </td>
                {/* 3. Mercato */}
                <td style={tdStyle()}>
                  <span style={{fontSize:12,color:"#475569"}}>{s.mercato || "—"}</span>
                </td>
                {/* 4. Eventi */}
                <td style={tdStyle("right")}>{(r.eventi||0).toLocaleString("it-IT")}</td>
                {/* 5. Punti (verde) */}
                <td style={{...tdStyle("right"),fontWeight:700,color:colPunti(r.punti),fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtPunti(r.punti)}
                </td>
                {/* 6. PPE (verde) */}
                <td style={{...tdStyle("right"),fontFamily:"'IBM Plex Mono',monospace",color:colPunti(r.ppe),fontWeight:600}}>
                  {fmtPunti(r.ppe)}
                </td>
                {/* 7. Max P/L (verde, senza segno +) */}
                <td style={{...tdStyle("right"),color:"#16A34A",fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNoSegno(r.max_pl)}
                </td>
                {/* 8. Quota Media */}
                <td style={tdStyle("right")}>{fmtQuota(r.quota_media_punta, r.quota_media_banca)}</td>
                {/* 9. Esp. Media (viola) */}
                <td style={{...tdStyle("right"),color:COL_VIOLA,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNum(r.esp_media, 2)}
                </td>
                {/* 10. Esp. Max Giorn. (viola) */}
                <td style={{...tdStyle("right"),color:COL_VIOLA,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNum(r.espo_max_giornaliera)}
                </td>
                {/* 11. Esp. Max Sett. (viola) */}
                <td style={{...tdStyle("right"),color:COL_VIOLA,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNum(r.espo_max_settimanale)}
                </td>
                {/* 10. Win Rate */}
                <td style={tdStyle("right")}>{fmtNum(r.pct_vincenti, 1)}%</td>
                {/* 11. Max Win Run (verde) */}
                <td style={{...tdStyle("right"),color:"#16A34A",fontWeight:600}}>{r.max_win_run}</td>
                {/* 12. Max Lose Run (rosso) */}
                <td style={{...tdStyle("right"),color:"#DC2626",fontWeight:600}}>{r.max_lose_run}</td>
                {/* 13. Max Drawdown (rosso) */}
                <td style={{...tdStyle("right"),color:"#DC2626",fontFamily:"'IBM Plex Mono',monospace"}}>{fmtNum(r.max_drawdown)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===== COMPONENTE PRINCIPALE =====
export default function PortfolioSistemi() {
  const [subTab, setSubTab] = useState("dettaglio"); // "dettaglio" | "trend"
  const [sistemi, setSistemi] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState(null);
  const [stagioniFiltro, setStagioniFiltro] = useState(new Set());
  const [dropdownAperta, setDropdownAperta] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({top:0,right:0});
  // Toggle filtri lista master Portfolio Sistemi (D485 v174)
  const [nascondiNonAttivi, setNascondiNonAttivi] = useState(false);
  const [nascondi5mko, setNascondi5mko] = useState(false);
  const dropdownRef = useRef(null);
  const btnDropdownRef = useRef(null);

  // Chiudi dropdown click fuori
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownAperta(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const caricaDati = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      const [rSis, rDash] = await Promise.all([
        fetch(`${API}/portfolio/sistemi?attivi_only=false`),
        fetch(`${API}/dashboard`),
      ]);
      if (!rSis.ok) throw new Error(`Sistemi: HTTP ${rSis.status}`);

      const dSis = await rSis.json();
      const dDash = rDash.ok ? await rDash.json() : null;

      setSistemi(Array.isArray(dSis) ? dSis : []);
      setDashboard(dDash);
    } catch(e) {
      setErrore(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { caricaDati(); }, [caricaDati]);

  // ===== STAGIONI DISPONIBILI =====
  const stagioni_disponibili = [...new Set(
    sistemi.flatMap(s => s.stagioni_lista || [])
  )].sort();

  const toggleStagione = (st) => {
    setStagioniFiltro(prev => {
      const next = new Set(prev);
      if (next.has(st)) next.delete(st); else next.add(st);
      return next;
    });
  };

  // KPI per un item in base alle stagioni selezionate
  const getKpi = (item) => {
    if (stagioniFiltro.size === 0) return { eventi: item.eventi, punti: item.punti, ppe: item.ppe };
    const entries = Object.entries(item.dati_per_stagione || {}).filter(([st]) => stagioniFiltro.has(st));
    if (!entries.length) return { eventi: 0, punti: 0, ppe: 0 };
    const ev = entries.reduce((a, [,v]) => a + v.eventi, 0);
    const pt = Math.round(entries.reduce((a, [,v]) => a + v.punti, 0) * 100) / 100;
    return { eventi: ev, punti: pt, ppe: ev > 0 ? Math.round(pt/ev*10000)/10000 : 0 };
  };

  // ===== FILTRAGGIO SISTEMI per toggle (D485 v174) =====
  const sistemiFiltrati = sistemi.filter(s => {
    if (nascondiNonAttivi && s.attivo === false) return false;
    if (nascondi5mko && s.filtri_5mko) return false;
    return true;
  });

  // ===== KPI GLOBALI =====
  const totPuntiSistemi   = sistemiFiltrati.reduce((acc, s) => acc + getKpi(s).punti, 0);
  const totEventiSistemi  = sistemiFiltrati.reduce((acc, s) => acc + getKpi(s).eventi, 0);
  const ppeMedioSistemi   = totEventiSistemi > 0
    ? sistemiFiltrati.reduce((acc, s) => { const k=getKpi(s); return acc + k.ppe * k.eventi; }, 0) / totEventiSistemi
    : 0;

  // P/L ciclo corrente da dashboard
  const cicloAttivo = dashboard?.ciclo?.attivo;
  const plCiclo = dashboard?.pl_ciclo_corrente ?? null;

  // ===== VISTA ANALISI SISTEMA EMBEDDED =====
  // Quando l'utente clicca una riga del Portfolio Sistemi, mostriamo
  // la scheda Analisi del sistema al posto della lista.
  const [sistemaInAnalisi, setSistemaInAnalisi] = useState(null);

  const handleClickSistema = (s) => {
    setSistemaInAnalisi(s.id);
  };

  // ===== EARLY RETURN: scheda Analisi del sistema cliccato =====
  if (sistemaInAnalisi) {
    return (
      <AnalisiSistemi
        sistemaIdIniziale={sistemaInAnalisi}
        onTornaIndietro={() => setSistemaInAnalisi(null)}
      />
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#FAFBFC",fontFamily:"'DM Sans',-apple-system,sans-serif"}}>

      {/* ===== HEADER ===== */}
      <div style={{padding:"24px 32px 0 32px"}}>
        <h1 style={{fontSize:22,fontWeight:700,color:"#1E293B",marginBottom:4,letterSpacing:"-0.02em"}}>
          💼 Portfolio Sistemi
        </h1>
        <p style={{fontSize:13,color:"#64748B",marginBottom:24}}>
          Analisi di performance e trend per i sistemi creati.
        </p>
      </div>

      {loading ? (
        <div style={{padding:60,textAlign:"center",color:"#94A3B8",fontSize:14}}>
          ⏳ Caricamento dati in corso…
        </div>
      ) : errore ? (
        <div style={{padding:"0 32px"}}>
          <div style={{background:"#FEF2F2",border:"1px solid #FECACA",borderRadius:10,
            padding:"14px 18px",color:"#DC2626",fontSize:13}}>
            ⚠️ Errore nel caricamento: {errore}
            <button onClick={caricaDati} style={{...btnPrimary,marginLeft:16,fontSize:11}}>
              🔄 Riprova
            </button>
          </div>
        </div>
      ) : (
        <div style={{padding:"0 32px 32px 32px"}}>

          {/* ===== BLOCCO 2 — TAB SISTEMI / STRATEGIE ===== */}
          <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden"}}>

            {/* ===== Barra blu navy — header "⚙️ Sistemi (N)" + dropdown stagioni (simmetria con Portfolio Strategie) ===== */}
            <div style={{display:"flex",background:"#1F2A4A",alignItems:"center"}}>

              {/* Gruppo sinistro: header Sistemi (N). Niente pulsante "+ Nuovo sistema" (funzionalità non presente). */}
              <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 16px"}}>
                <span style={{
                  padding:"12px 8px", fontSize:13, fontWeight:700,
                  color:"#fff", fontFamily:"'DM Sans',sans-serif",
                }}>
                  ⚙️ Sistemi ({sistemi.length})
                </span>
              </div>

              <div style={{flex:1}}/>

              {/* Gruppo destro: dropdown stagione + chip selezionate (solo sub-tab Dettaglio) */}
              <div style={{display:"flex",alignItems:"center",paddingRight:16,gap:8}}>

                {subTab === "dettaglio" && stagioni_disponibili.length > 0 && (
                  <div ref={dropdownRef} style={{position:"relative"}}>
                    <button ref={btnDropdownRef} onClick={() => {
                        if (!dropdownAperta && btnDropdownRef.current) {
                          const r = btnDropdownRef.current.getBoundingClientRect();
                          setDropdownPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                        }
                        setDropdownAperta(o => !o);
                      }}
                      style={{...btnBase,background:"#F1F5F9",color:"#475569",fontSize:11,
                        padding:"6px 12px",border:"1px solid #E8E8E4"}}>
                      {stagioniFiltro.size === 0
                        ? "📅 Stagione ▾"
                        : `📅 ${stagioniFiltro.size} stagion${stagioniFiltro.size===1?"e":"i"} ▾`}
                    </button>
                    {dropdownAperta && (
                      <div style={{position:"fixed",top:dropdownPos.top,right:dropdownPos.right,
                        background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
                        boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:9999,
                        minWidth:160,padding:"6px 0"}}>
                        <div onClick={() => setStagioniFiltro(new Set())}
                          style={{padding:"6px 14px",fontSize:12,cursor:"pointer",
                            display:"flex",alignItems:"center",gap:8,
                            background: stagioniFiltro.size===0 ? "#EFF6FF" : "transparent",
                            color: stagioniFiltro.size===0 ? "#2563EB" : "#374151"}}>
                          <span style={{width:14,height:14,borderRadius:3,border:"1px solid #CBD5E1",
                            display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,
                            background: stagioniFiltro.size===0 ? "#2563EB" : "#fff",
                            color:"#fff"}}>
                            {stagioniFiltro.size===0 ? "✓" : ""}
                          </span>
                          Tutte
                        </div>
                        <div style={{height:1,background:"#F1F5F9",margin:"4px 0"}}/>
                        <div style={{maxHeight:220,overflowY:"auto"}}>
                          {stagioni_disponibili.map(st => (
                            <div key={st} onClick={() => toggleStagione(st)}
                              style={{padding:"6px 14px",fontSize:12,cursor:"pointer",
                                display:"flex",alignItems:"center",gap:8,
                                background: stagioniFiltro.has(st) ? "#EFF6FF" : "transparent",
                                color: stagioniFiltro.has(st) ? "#2563EB" : "#374151"}}>
                              <span style={{width:14,height:14,borderRadius:3,border:"1px solid #CBD5E1",
                                display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,
                                background: stagioniFiltro.has(st) ? "#2563EB" : "#fff",
                                color:"#fff"}}>
                                {stagioniFiltro.has(st) ? "✓" : ""}
                              </span>
                              {st}
                            </div>
                          ))}
                        </div>
                        <div style={{height:1,background:"#F1F5F9",margin:"4px 0"}}/>
                        <div onClick={() => setDropdownAperta(false)}
                          style={{padding:"6px 14px",fontSize:12,cursor:"pointer",
                            textAlign:"center",color:"#2563EB",fontWeight:600}}>
                          Chiudi
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {subTab === "dettaglio" && stagioniFiltro.size > 0 && [...stagioniFiltro].sort().map(st => (
                  <span key={st} style={{fontSize:11,fontWeight:700,padding:"3px 8px",
                    borderRadius:12,background:"#2563EB",color:"#fff",
                    display:"inline-flex",alignItems:"center",gap:4}}>
                    {st}
                    <button onClick={() => toggleStagione(st)}
                      style={{background:"none",border:"none",cursor:"pointer",
                        color:"#fff",fontSize:12,padding:0,lineHeight:1}}>×</button>
                  </span>
                ))}

              </div>
            </div>

            {/* Sub-tab bar (Dettaglio stagione / Trend storico) */}
            <div style={{display:"flex", background:"#F1F5F9",
              borderBottom:"1px solid #E8E8E4", padding:"0 16px", alignItems:"center"}}>
              {[
                {id:"dettaglio", label:"📊 Dettaglio stagione", info:null},
                {id:"trend",     label:"📈 Trend storico",      info:true},
              ].map(t => (
                <div key={t.id} style={{display:"inline-flex", alignItems:"center"}}>
                  <button onClick={() => setSubTab(t.id)}
                    style={{
                      padding:"8px 16px", fontSize:12, fontWeight:600,
                      border:"none", cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                      background:"transparent", transition:"all 0.15s",
                      borderBottom: subTab === t.id ? "2px solid #2563EB" : "2px solid transparent",
                      color: subTab === t.id ? "#2563EB" : "#64748B",
                      marginBottom:"-1px",
                    }}>
                    {t.label}
                  </button>
                  {t.info && subTab === t.id && (
                    <IconaInfoModal titolo="Come leggere il Trend storico">
                      <p style={{margin: "0 0 12px 0"}}>
                        Confronto delle ultime 5 stagioni per ogni sistema. Per ogni stagione vedi:
                      </p>
                      <ul style={{margin: "0 0 12px 0", paddingLeft: 20}}>
                        <li style={{marginBottom: 4}}><b>Ingressi</b>: numero di ingressi a mercato nella stagione</li>
                        <li style={{marginBottom: 4}}><b>Punti</b>: P/L totale della stagione</li>
                        <li style={{marginBottom: 4}}><b>PPE</b> (Punti Per Evento): redditività media per singolo evento</li>
                      </ul>
                      <p style={{margin: "0 0 12px 0"}}>
                        La <b>colonna evidenziata in azzurro</b> è la stagione corrente. Quando è ancora in corso, accanto al codice trovi la % di completamento (es. <code style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:12}}>25-26 (35%)</code>).
                      </p>
                      <p style={{margin: "0 0 12px 0"}}>
                        Accanto al nome di ogni sistema trovi la <b>Performance storica</b>: una valutazione sintetica del rendimento complessivo (Ottima · Media · Debole) corredata da uno score 0-100%. Per i dettagli del calcolo, clicca "Come si calcola" nella legenda.
                      </p>
                      <p style={{margin: "0 0 8px 0"}}>
                        Le <b>due freccette</b> accanto a Punti e PPE indicano il <b>Trend stagionale</b>:
                      </p>
                      <ul style={{margin: "0 0 12px 0", paddingLeft: 20}}>
                        <li style={{marginBottom: 4}}><b>1ª freccia</b> → confronto con la stagione immediatamente precedente</li>
                        <li style={{marginBottom: 4}}><b>2ª freccia</b> → confronto con la media delle 3 stagioni precedenti chiuse (può includere stagioni non visibili in tabella, se il sistema ha più storico)</li>
                      </ul>
                      <p style={{margin: 0}}>
                        Una freccia <span style={{color:"#CBD5E1"}}>–</span> grigia indica che il confronto non è disponibile (es. servono almeno 3 stagioni chiuse per la media). Passa il mouse sulla cella per i valori numerici esatti.
                      </p>
                    </IconaInfoModal>
                  )}
                </div>
              ))}
              <div style={{flex:1}}/>
              {/* Toggle filtri lista master Portfolio Sistemi (D485 v174) — solo sub-tab Dettaglio */}
              {subTab === "dettaglio" && (
                <div style={{display:"flex",gap:8,paddingRight:8}}>
                  <label title="Nasconde i sistemi mai attivati e quelli dismessi. Restano tutti in elenco togliendo la spunta."
                    style={{display:"inline-flex",alignItems:"center",gap:6,
                    fontSize:11,color:"#475569",cursor:"pointer",userSelect:"none"}}>
                    <input type="checkbox" checked={nascondiNonAttivi}
                      onChange={e => setNascondiNonAttivi(e.target.checked)}
                      style={{cursor:"pointer"}}/>
                    Nascondi non attivi
                  </label>
                  <label style={{display:"inline-flex",alignItems:"center",gap:6,
                    fontSize:11,color:"#475569",cursor:"pointer",userSelect:"none"}}>
                    <input type="checkbox" checked={nascondi5mko}
                      onChange={e => setNascondi5mko(e.target.checked)}
                      style={{cursor:"pointer"}}/>
                    Nascondi 5MKO
                  </label>
                </div>
              )}
            </div>

            {/* Contenuto tab */}
            <div style={{padding:0}}>
              {subTab === "dettaglio" ? (
                <TabellaSistemi dati={sistemiFiltrati} onClickRiga={handleClickSistema} stagioniFiltro={stagioniFiltro} />
              ) : (
                <PortfolioTrendStorico items={sistemiFiltrati} tipo="sistema" />
              )}
            </div>

            {/* Footer tabella — solo nel sub-tab Dettaglio stagione */}
            {subTab === "dettaglio" && sistemiFiltrati.length > 0 && (
              <div style={{padding:"10px 16px",background:"#F8FAFC",
                borderTop:"1px solid #E8E8E4",fontSize:11,color:"#94A3B8",
                display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>{sistemiFiltrati.length} sistem{sistemiFiltrati.length===1?"a":"i"} · {totEventiSistemi.toLocaleString("it-IT")} eventi totali</span>
                <span>Punti totali: <strong style={{color:colPunti(totPuntiSistemi)}}>{fmtPunti(totPuntiSistemi)}</strong></span>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
